// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import emailPreviewApi from '../api/emailPreviewApi';
import { sendCoaNudge } from '../api/coaNudgeApi';
import type { EmailPolicyPreview, EmailPreviewResponse } from '../types/emailPreview';
import type { NudgeDataResponse, NudgeRequest, NudgeResult } from '../types/nudge';

/**
 * Adapter — maps the unified {@link EmailPreviewResponse} into the
 * legacy {@link NudgeDataResponse} shape the composer page + history
 * display were built against. Preserves the policy's
 * {@code agencyCode} (the CoA-specific datum) on the adapted
 * {@code policyInfo} so the UI can render it.
 */
function adaptPreviewToCoaNudgeData(
  preview: EmailPreviewResponse,
  policyId: string,
): NudgeDataResponse & { agencyCode: string | null } {
  const policy = preview.policies[0];
  const recipient = preview.recipients[0];
  return {
    policyInfo: {
      policyId: policy?.policyId ?? policyId,
      policyNumber: policy?.policyNumber ?? null,
      providerId: recipient?.id ?? '',
      providerName: recipient?.name ?? policy?.providerName ?? '',
      providerEmail: recipient?.email ?? '',
      policyTypeName: policy?.policyTypeName ?? null,
      profileId: '',
      profileName: '',
      hasLoaSlice: false,
    },
    loaInfo: {
      loaId: '',
      loaReference: '',
      status: '',
      completedAt: '',
    },
    defaultTemplate: {
      subject: preview.subject,
      bodyHtml: preview.editableBodyHtml,
    },
    // CoA nudge doesn't currently carry a rate-limit or history payload
    // in the preview response; we synthesise empty defaults so the shared
    // composer widgets don't crash on undefined.
    rateLimitStatus: {
      warningCode: 'NONE',
      nudgesForPolicyToday: 0,
      nudgesForProviderToday: 0,
      nudgesForProviderThisWeek: 0,
      nudgesForProviderThisMonth: 0,
      totalNudgesForPolicy: 0,
      thresholds: {
        maxPerPolicyPerDay: 0,
        maxPerProviderPerDay: 0,
        maxPerProviderPerWeek: 0,
        maxPerProviderPerMonth: 0,
        minHoursBetweenNudges: 0,
      },
    },
    nudgeHistory: {
      policyId,
      totalNudgeCount: 0,
      successfulNudgeCount: 0,
      failedNudgeCount: 0,
      entries: [],
    },
    agencyCode: policy?.agencyCode ?? null,
  };
}

/**
 * Hook to fetch CoA nudge preview data via POST /api/email-preview.
 * Returns the same shape as {@link useNudgeData} plus the
 * CoA-specific {@code agencyCode} for the policy card.
 */
export const useCoaNudgeData = (coaId: string, policyId: string, enabled = true) => {
  const [data, setData] = useState<(NudgeDataResponse & { agencyCode: string | null }) | null>(null);
  const [policies, setPolicies] = useState<EmailPolicyPreview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !coaId || !policyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const preview = await emailPreviewApi.preview({
        kind: 'COA_PROVIDER_NUDGE',
        coaId,
        policyId,
      });
      setData(adaptPreviewToCoaNudgeData(preview, policyId));
      setPolicies(preview.policies);
    } catch (err) {
      console.error('Failed to fetch CoA nudge data:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch CoA nudge data'));
    } finally {
      setIsLoading(false);
    }
  }, [coaId, policyId, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, policies, isLoading, error, refetch: fetchData };
};

/** Mutation hook for sending an advisor-triggered CoA nudge. */
export const useSendCoaNudge = (coaId: string, policyId: string) => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = useCallback(
    async (request: NudgeRequest): Promise<NudgeResult> => {
      setIsPending(true);
      setError(null);
      try {
        return await sendCoaNudge(coaId, policyId, request);
      } catch (err) {
        const e = err instanceof Error ? err : new Error('Failed to send CoA nudge');
        setError(e);
        throw e;
      } finally {
        setIsPending(false);
      }
    },
    [coaId, policyId],
  );

  return { mutateAsync, isPending, error };
};
